import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import PropTypes from 'prop-types';
import Button from './Button';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { deleteClinicalService, deleteMedicationReview, fetchClinicalServices } from '../../redux/modules/clinical-services/actions';
import store from '../../redux/store';

const ONE_HOUR_MS = 60 * 60 * 1000;
const FOUR_HOURS_MS = 4 * 60 * 60 * 1000;

const isTechnicalIssueReason = (reason) => {
  const normalizedReason = normalizeResumeReason(reason);
  return normalizedReason === 'technical-issue' || normalizedReason === 'technical issue' || normalizedReason === 'technical_issue';
};

// Returns one of: 'autoResume', 'showModal', 'redirectDashboard'
export function evaluateResumeDecision(
  lastUpdatedAt,
  resumeReason = 'inactivity',
  hasActiveService = true,
  now = Date.now()
) {
  // Backward compatibility: older calls may pass evaluateResumeDecision(lastUpdatedAt, now)
  if (typeof resumeReason === 'number') {
    now = resumeReason;
    resumeReason = 'inactivity';
    hasActiveService = true;
  } else if (typeof hasActiveService === 'number') {
    now = hasActiveService;
    hasActiveService = true;
  }

  if (!lastUpdatedAt) return 'autoResume';

  const lastUpdatedMs = new Date(lastUpdatedAt).getTime();
  if (Number.isNaN(lastUpdatedMs)) return 'autoResume';

  const diffMs = Math.max(0, now - lastUpdatedMs);
  if (diffMs < ONE_HOUR_MS) return 'autoResume';
  if (diffMs >= FOUR_HOURS_MS) return 'redirectDashboard';
  if (!hasActiveService) return 'redirectDashboard';

  return 'showModal';
}

// Simple human-friendly "time ago" helper
export function timeAgo(timestamp, now = Date.now()) {
  if (!timestamp) return 'Unknown';
  const diff = Math.max(0, now - new Date(timestamp).getTime());
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return `${seconds} seconds ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days > 1 ? 's' : ''} ago`;
}

const normalizeResumeReason = (reason) => String(reason || 'timeout').toLowerCase();

const formatServiceDateTime = (timestamp) => {
  if (!timestamp) return null;
  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return null;
  const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
  const day = String(date.getDate()).padStart(2, '0');
  const month = months[date.getMonth()];
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return { date: `${day}-${month}-${year}`, time: `${hours}${minutes}hrs` };
};

export default function ResumeClinicalServiceModal({ open, onResume, onStartNew, onClose, resumeTargetId, resumeRecord, lastAccessedBy, patientId: propPatientId }) {
  const [visible, setVisible] = useState(Boolean(open));
  const navigate = useNavigate();
  const params = useParams();
  const dispatch = useDispatch();
  const clinicalServicesSmall = useSelector((state) => state.clinicalService.clinicalServicesSmall);

  const patientId = propPatientId || params?.id;

  useEffect(() => {
    if (visible && patientId && !clinicalServicesSmall) {
      dispatch(fetchClinicalServices(patientId));
    }
  }, [visible, patientId, clinicalServicesSmall, dispatch]);

  useEffect(() => {
    setVisible(Boolean(open));
  }, [open]);

  const handleClose = () => {
    setVisible(false);
    if (onClose) {
      onClose();
    }
  };

  const handleXClose = () => {
    setVisible(false);
    if (onClose) {
      onClose();
    }
  };

  const isInProgress = (status) => {
    if (!status) return false;
    return /\bin[\s_-]?progress\b/i.test(String(status));
  };

  const findRunningService = (small) => {
    const runningMR = (small?.medicationReviews || []).find((s) => isInProgress(s?.status));
    const runningMA = (small?.minorAilments || []).find((s) => isInProgress(s?.status));
    return runningMR || runningMA || null;
  };

  const runningServiceFromStore = findRunningService(clinicalServicesSmall);

  const resolvedLastUpdatedAt =
    resumeRecord?.date ||
    resumeRecord?.systemPeriod?.LowerBound ||
    resumeRecord?.initialAssessment?.lastUpdatedAt ||
    resumeRecord?.updatedAt ||
    runningServiceFromStore?.updatedAt ||
    runningServiceFromStore?.date ||
    null;

  const formattedDateTime = formatServiceDateTime(resolvedLastUpdatedAt);

  const handleResume = async () => {
    if (onResume) {
      onResume();
      setVisible(false);
      return;
    }

    let runningService = findRunningService(clinicalServicesSmall);

    if (!runningService && patientId) {
      try {
        await dispatch(fetchClinicalServices(patientId));
        const latest = store.getState().clinicalService.clinicalServicesSmall;
        runningService = findRunningService(latest);
      } catch (e) {
        // ignore fetch errors
      }
    }

    if (patientId) {
      if (runningService) {
        const routeId = runningService.id || runningService.minorAilmentId || runningService.medReviewId || runningService.initialAssessmentId || runningService.clinicalServiceId;
        navigate(`/patient/${patientId}/clinical-services/${routeId || ''}`);
      } else if (resumeTargetId) {
        navigate(`/patient/${patientId}/clinical-services/${resumeTargetId}`);
      } else {
        navigate(`/patient/${patientId}/clinical-services/`);
      }
    }
    setVisible(false);
  };

  const handleStartNew = async () => {
    const runningMA = clinicalServicesSmall?.minorAilments?.find((s) => isInProgress(s?.status));
    const runningMR = clinicalServicesSmall?.medicationReviews?.find((s) => isInProgress(s?.status));
    const runningService = runningMR || runningMA;

    try {
      if (runningService && patientId) {
        if (runningService.medicationReviewTypeCode) {
          await dispatch(deleteMedicationReview(patientId, runningService.id));
        } else {
          await dispatch(deleteClinicalService(patientId, runningService.id));
        }
        await dispatch(fetchClinicalServices(patientId));
      }

      if (onStartNew) {
        onStartNew();
      } else {
        navigate(`/patient/${patientId}/clinical-services/`);
      }
    } catch (e) {
      console.error('Cancel failed', e);
    } finally {
      setVisible(false);
    }
  };

  return createPortal(
    <Transition
      enter="transition duration-200 ease-out"
      enterFrom="transform scale-95 opacity-0"
      enterTo="transform scale-100 opacity-100"
      leave="transition duration-200 ease-out"
      leaveFrom="transform scale-100 opacity-100"
      leaveTo="transform scale-95 opacity-0"
      className="fixed z-[50] top-0 left-0 w-full h-screen"
      show={visible}
    >
      <div className="fixed inset-0 bg-black/10 z-[1]" aria-hidden="true" />
      <Transition.Child
        enter="transition duration-200 ease-out"
        enterFrom="transform scale-95 opacity-0"
        enterTo="transform scale-100 opacity-100"
        leave="transition duration-200 ease-out"
        leaveFrom="transform scale-100 opacity-100"
        leaveTo="transform scale-95 opacity-0"
        className="bg-white z-[2] flex flex-col absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-[10px] w-[536px] shadow-[0px_8px_8px_rgba(0,0,0,0.08)]"
      >
        <button
          type="button"
          onClick={handleXClose}
          className="absolute top-4 right-4 flex items-center justify-center p-[6px] transition-all rounded-full cursor-pointer bg-blue-200 hover:bg-REELTeal hover:text-white text-teal-300"
          aria-label="Close"
        >
          <span className="sr-only">Close panel</span>
          <XMarkIcon className="w-5 h-5 m-auto" strokeWidth="2" aria-hidden="true" />
        </button>

        <div className="px-8 pt-8 pb-4 flex justify-center">
          <h3 className="text-teal-300 font-semibold text-2xl-body leading-2xl-body text-center">
            Clinical Service in Progress
          </h3>
        </div>

        <div className="flex flex-col gap-[30px] px-8 pb-8">
          <div className="border-t border-gray-100 w-full">
            <p className="text-lg-body font-semibold text-teal-300 leading-[24px] pt-4">
              A Clinical Service was started but not completed.
            </p>
          </div>

          <div className="flex flex-col gap-[25px] text-md-body text-gray-800">
            <p className="leading-[22px]">
              Review the patient&apos;s current condition before continuing.<br />
              If eligibility or timing has changed, start a new service.
            </p>
            {(lastAccessedBy || formattedDateTime) && (
              <div className="leading-[22px]">
                {lastAccessedBy && (
                  <p>Last accessed by: <span className="font-medium">{lastAccessedBy}</span></p>
                )}
                {formattedDateTime && (
                  <p>
                    {'Last updated on:   '}
                    <span className="font-medium">{formattedDateTime.date}</span>
                    {' at '}
                    <span className="font-medium">{formattedDateTime.time}</span>
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="flex flex-col gap-4 items-center">
            <Button type="primary" text="Resume Service" onClick={handleResume} className="!w-[400px] !h-[52px]" />
            <Button type="primary-outline" text="Cancel & Start New Service" onClick={handleStartNew} className="!w-[400px] !h-[52px]" />
            <Button type="primary-outline" text="Close" onClick={handleClose} className="!w-[400px] !h-[52px]" />
          </div>
        </div>
      </Transition.Child>
    </Transition>,
    document.body
  );
}

ResumeClinicalServiceModal.propTypes = {
  open: PropTypes.bool,
  onResume: PropTypes.func,
  onStartNew: PropTypes.func,
  onClose: PropTypes.func,
  resumeTargetId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  resumeRecord: PropTypes.object,
  resumeReason: PropTypes.string,
  lastAccessedBy: PropTypes.string,
  patientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

ResumeClinicalServiceModal.defaultProps = {
  open: false,
  onResume: null,
  onStartNew: null,
  onClose: null,
  resumeTargetId: null,
  resumeRecord: null,
  resumeReason: 'timeout',
  lastAccessedBy: null,
  patientId: null,
};
